import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Box } from "../../../common/components";
import { ProductImage } from "../../../components/StudioToolbar/ProductImage";
import StudioToolbar, {
  STUDIO_TOOLBAR_HEIGHT,
} from "../../../components/StudioToolbar/StudioToolbar";
import {
  FLOATING_PREVIEW_ASSET_TEAM_ID,
  ROUTES,
} from "../../../constant/common.constant";
import { useAppLoading } from "../../../context/AppLoading/useAppLoading";
import { StudioContextProvider } from "../../../context/Studio/StudioContextProvider";
import { useDesignState } from "../../../utils/ReactHooks";
import { useThemeBreakpoints } from "../../../utils/themeUtils";
import StudioLeftSidebar from "../../StudioSidebarContainer/StudioLeftSidebar/StudioLeftSidebar";
import { StudioRightSidebar } from "../../StudioSidebarContainer/StudioRightSidebar";
import MobileBottomBar from "../components/MobileBottomBar";

const SpecsPreviewStudioContainer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { designId } = useParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useThemeBreakpoints();
  const { setIsAppLoading } = useAppLoading();

  const designState = useDesignState(designId, true);
  const {
    isProductDefLoading,
    hasLoadingFailed,
    loadingFailedErrorKey,
    designFromDesignState,
    specRevisionId,
  } = designState;

  const productName = designFromDesignState?.product.name;
  const productVersion = designFromDesignState?.product.version;
  const shortSpecRevisionId = specRevisionId?.split("-")[0];
  const previewDesignName =
    specRevisionId || [productName, productVersion].filter(Boolean).join(" ");

  useEffect(() => {
    if (hasLoadingFailed) {
      toast.error(t(`errors.toast.${loadingFailedErrorKey}`), {
        toastId: loadingFailedErrorKey,
      });
      setIsAppLoading(false);
      navigate(ROUTES.HOME, { replace: true });
    }
  }, [hasLoadingFailed, loadingFailedErrorKey, navigate, setIsAppLoading, t]);

  useEffect(() => {
    setIsAppLoading(isProductDefLoading);

    return () => {
      setIsAppLoading(false);
    };
  }, [isProductDefLoading, setIsAppLoading]);

  useEffect(() => {
    containerRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  }, []);

  if (!designId || !productName) {
    return null;
  }

  return (
    <StudioContextProvider
      value={{
        designState,
        designId,
        productName,
        projectId: "",
        teamId: FLOATING_PREVIEW_ASSET_TEAM_ID,
        projectName: undefined,
        editorConfig: {
          allowMissingProjectContext: true,
          hideArtworkLibrary: true,
          toolbar: {
            topLeftLabel: shortSpecRevisionId
              ? `Specs revision: ${shortSpecRevisionId}`
              : undefined,
            breadcrumbs: {
              backPageRoute: "",
              backPageLabel: "",
              backPageTooltip: "",
            },
            hideDesignMenu: true,
            hideMiddleSection: true,
            hideAddToCartButton: true,
            hideCurrentUsers: true,
            hideInviteUserButton: true,
            hideDesignSelector: true,
          },
        },
      }}
    >
      <Box ref={containerRef} maxHeight={"100dvh"} data-test="studioContainer">
        <Box>
          <StudioToolbar designName={previewDesignName} />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            justifyContent: { xs: "space-between", lg: undefined },
          }}
          minHeight={{
            xs: `calc(100dvh - ${STUDIO_TOOLBAR_HEIGHT})`,
            lg: `calc(100dvh - ${STUDIO_TOOLBAR_HEIGHT})`,
          }}
          maxHeight={{
            xs: `calc(100dvh - ${STUDIO_TOOLBAR_HEIGHT})`,
            lg: `calc(100dvh - ${STUDIO_TOOLBAR_HEIGHT})`,
          }}
        >
          <Box
            sx={{ display: "flex", overflow: "hidden" }}
            width={"100%"}
            flex={1}
          >
            <React.Fragment>
              {!isMobile && <StudioLeftSidebar />}
              <ProductImage previewContainerRef={previewContainerRef} />
              {!isMobile && <StudioRightSidebar />}
            </React.Fragment>
          </Box>
          {isMobile && <MobileBottomBar />}
        </Box>
      </Box>
    </StudioContextProvider>
  );
};

export default SpecsPreviewStudioContainer;
